/**
 * adminActivityLogStore — Activity Logs slice of the admin store (Zustand).
 * Exposes the activity-logs endpoints (`/api/admin/activity-logs`).
 *
 * Endpoints:
 *   GET  /api/admin/activity-logs              — list (supports page, limit,
 *          actorType, actorId, action, targetType, targetId, from, to, sort)
 *   GET  /api/admin/activity-logs/:id          — single record detail
 */
import { authHeaders, resolveToken } from "./adminUtils";
import { toDisplayString } from "../../utils/toDisplayString";
import { API_URL } from "../../constants";

export const activityLogInitialState = {
  // List
  activityLogs: [],
  activityLogsLoading: false,
  activityLogsError: null,
  activityLogTotal: 0,
  activityLogPages: 1,

  // Single activity log
  activityLogDetail: null,
  activityLogDetailLoading: false,
  activityLogDetailError: null,
};

/** Normalize any list payload shape into `{ items, total, totalPages }`. */
function normalizeListPayload(data) {
  const payload = data?.data || data || {};
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, totalPages: 1 };
  }
  return {
    items: payload.items || payload.activityLogs || [],
    total: payload.total ?? payload.totalItems ?? 0,
    totalPages: payload.totalPages ?? payload.pages ?? 1,
  };
}

export function createActivityLogActions(set, get) {
  return {
    // ── GET /api/admin/activity-logs ────────────────────────────────
    fetchActivityLogs: async (params = {}) => {
      set({ activityLogsLoading: true, activityLogsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ).toString();
        const response = await fetch(
          `${API_URL}/api/admin/activity-logs${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        const data = await response.json();
        if (data.error) {
          set({ activityLogsLoading: false, activityLogsError: toDisplayString(data.error, "Request failed")});
          return { success: false, error: toDisplayString(data.error, "Request failed")};
        }
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          activityLogsLoading: false,
          activityLogs: items,
          activityLogTotal: total,
          activityLogPages: totalPages,
        });
        return { success: true, activityLogs: items, total, pages: totalPages };
      } catch (err) {
        set({
          activityLogsLoading: false,
          activityLogsError: err.message,
          activityLogs: [],
        });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/activity-logs/:id ─────────────────────────────
    fetchActivityLogById: async (id) => {
      set({ activityLogDetailLoading: true, activityLogDetailError: null });
      try {
        const response = await fetch(`${API_URL}/api/admin/activity-logs/${id}`, {
          headers: authHeaders(resolveToken(get)),
        });
        const data = await response.json();
        if (data.error) {
          set({
            activityLogDetailLoading: false,
            activityLogDetailError: toDisplayString(data.error, "Request failed"),
          });
          return { success: false, error: toDisplayString(data.error, "Request failed")};
        }
        const detail = data?.data || data || null;
        set({
          activityLogDetailLoading: false,
          activityLogDetail: detail,
        });
        return { success: true, activityLog: detail };
      } catch (err) {
        set({
          activityLogDetailLoading: false,
          activityLogDetailError: err.message,
          activityLogDetail: null,
        });
        return { success: false, error: err.message };
      }
    },
  };
}

const activityLogActions = createActivityLogActions;
export default activityLogActions;